import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Phone, MapPin, Clock, Mail, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const Contact = ({ currentLang, translations }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
        language: currentLang
      });

      if (response.data.success) {
        toast.success(
          currentLang === 'he' ? 'הודעתכם נשלחה בהצלחה! נחזור אליכם בקרוב.' :
          currentLang === 'ru' ? 'Ваше сообщение успешно отправлено! Мы свяжемся с вами в ближайшее время.' :
          'Your message has been sent successfully! We will contact you soon.'
        );
        setFormData({ name: '', phone: '', service: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error(
        currentLang === 'he' ? 'שגיאה בשליחת ההודעה. אנא נסו שוב.' :
        currentLang === 'ru' ? 'Ошибка при отправке сообщения. Пожалуйста, попробуйте еще раз.' :
        'Error sending message. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: translations[currentLang].contact.phone,
      value: '+972-55-300-2025',
      action: () => window.open('tel:+972553002025')
    },
    {
      icon: MapPin,
      label: translations[currentLang].contact.address,
      value: 'Zunz 30, Tel Aviv'
    },
    {
      icon: Clock,
      label: translations[currentLang].contact.hours,
      value: translations[currentLang].contact.emergency
    }
  ];

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            {translations[currentLang].contact.title}
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            {translations[currentLang].contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              {translations[currentLang].contact.contact}
            </h3>
            
            <div className="space-y-6">
              {contactInfo.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-grow">
                          <h4 className="font-semibold text-gray-900 mb-1">{item.label}</h4>
                          {item.action ? (
                            <button 
                              onClick={item.action}
                              className="text-blue-600 hover:text-blue-700 transition-colors font-medium"
                            >
                              {item.value}
                            </button>
                          ) : (
                            <p className="text-gray-600">{item.value}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Emergency Call Button */}
            <Card className="bg-gradient-to-r from-red-50 to-red-100 border-red-200">
              <CardContent className="p-6 text-center">
                <h4 className="text-xl font-bold text-gray-900 mb-4">
                  {translations[currentLang].services.emergency.title}
                </h4>
                <Button 
                  size="lg"
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 text-lg flex items-center space-x-3 mx-auto"
                  onClick={() => window.open('tel:+972553002025')}
                >
                  <Phone className="w-5 h-5" />
                  <span>{translations[currentLang].services.emergency.callNow}</span>
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div>
            <Card className="shadow-xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  {translations[currentLang].contact.form.send}
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Input
                      placeholder={translations[currentLang].contact.form.name}
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="h-12"
                    />
                  </div>
                  
                  <div>
                    <Input
                      type="tel"
                      placeholder={translations[currentLang].contact.form.phone}
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                      className="h-12"
                    />
                  </div>
                  
                  <div>
                    <Input
                      placeholder={translations[currentLang].contact.form.service}
                      value={formData.service}
                      onChange={(e) => setFormData({...formData, service: e.target.value})}
                      className="h-12"
                    />
                  </div>
                  
                  <div>
                    <Textarea
                      placeholder={translations[currentLang].contact.form.placeholder}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      required
                      rows={5}
                      className="resize-none"
                    />
                  </div>
                  
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {currentLang === 'he' ? 'שולח...' : 
                         currentLang === 'ru' ? 'Отправка...' : 
                         'Sending...'}
                      </>
                    ) : (
                      translations[currentLang].contact.form.send
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;